import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Observable } from 'rxjs';
import { map, catchError } from 'rxjs/operators';
import {
  CreateFileLibraryRequest,
  CreateFileLibraryResponse,
  FileLibrarySpecimenResponse
} from '@model/laboratory/library/file-library/file-library.model';
import { HttpErrorHandlerService } from '../../../core/error-handling/http-error-handler.service';
import { environment } from 'src/environments/environment';
import { FileLibraryRepository } from '@service/laboratory/file-library/file-library-repository';
import {
  CreateFileLibraryBackendResponse,
  FileLibrarySpecimenBackendDTO
} from './file-library-backend.dto';

/**
 * File Library Repository Adapter
 *
 * Implements the FileLibraryRepository interface defined in the domain layer.
 * Handles HTTP communication for file-based library creation.
 *
 * Architecture Layer: Infrastructure
 * - Implements domain interface (Dependency Inversion)
 * - Uses HttpClient for HTTP operations
 * - Transforms backend DTOs to domain models
 */
@Injectable({
  providedIn: 'root'
})
export class FileLibraryRepositoryAdapter extends FileLibraryRepository {
  private readonly http = inject(HttpClient);
  private readonly BASE_URL = `${environment.laboratory.baseUrl}${environment.laboratory.context}`;
  private readonly errorHandler = inject(HttpErrorHandlerService);

  /**
   * Create libraries from file upload with grouped samples
   *
   * @param request Request with user ID and library groups
   * @returns Observable with array of created library information
   */
  createLibrariesFromFile(
    request: CreateFileLibraryRequest
  ): Observable<CreateFileLibraryResponse[]> {
    return this.http
      .post<
        CreateFileLibraryBackendResponse[]
      >(`${this.BASE_URL}/library/create`, request)
      .pipe(
        map((response) => {
          if (!response) {
            throw new Error('No response from server');
          }
          return response.map((item) => this.mapToResponse(item));
        }),
        catchError((error: HttpErrorResponse) =>
          this.errorHandler.handleError('createLibrariesFromFile', error)
        )
      );
  }

  /**
   * Map backend DTO to domain model
   */
  private mapToResponse(
    dto: CreateFileLibraryBackendResponse
  ): CreateFileLibraryResponse {
    return {
      libraryId: dto.libraryId,
      libraryCode: dto.libraryCode,
      status: dto.status,
      sampleCount: dto.sampleCount,
      specimens:
        dto.specimens?.map((specimen) =>
          this.mapToSpecimenResponse(specimen)
        ) || [],
      createdAt: new Date(dto.createdAt),
      createdByUserId: dto.createdByUserId
    };
  }

  /**
   * Map specimen backend DTO to domain model
   */
  private mapToSpecimenResponse(
    dto: FileLibrarySpecimenBackendDTO
  ): FileLibrarySpecimenResponse {
    return {
      libSpecimenId: dto.libSpecimenId,
      specimenId: dto.specimenId,
      indexId: dto.indexId,
      lgCode: dto.lgCode,
      assignmentDate: new Date(dto.assignmentDate)
    };
  }
}
